import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import TariffImportDialog from 'Services/ProductManagement/Features/Tariff/Components/TariffImportDialog'
import TariffList from 'Services/ProductManagement/Features/Tariff/Components/TariffList'
import { TariffContext } from 'Services/ProductManagement/Features/Tariff/Context/tariffContext'
import { useTariffsWithParams } from 'Services/ProductManagement/Features/Tariff/Hooks'
import {
  ENABLE_TARIFF_IMPORT,
  PRODUCT_MANAGEMENT_ROOT_URL,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import AddIcon from '@mui/icons-material/Add'
import UploadIcon from '@mui/icons-material/Upload'
import { Box, Typography } from '@mui/material'
import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

function TariffListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()

  const { tariffs, loading, total_results, params, setParams } = useTariffsWithParams()
  const [searchText, setSearchText] = useState(params.search_text ?? '')
  const { setSelectedTariffId } = useContext(TariffContext)
  setSelectedTariffId('')
  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`
    }
  ]
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleImportDialogOpen = () => {
    setImportDialogOpen(true)
  }

  const handleImportDialogClose = () => {
    setImportDialogOpen(false)
  }

  const isLoading = loading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Tariffs`}</Typography>
        <Box mt={4}>
          <ListActions>
            <Box display="flex" gap={2}>
              <CustomButton
                onClick={() => navigate(`${baseUrl}/create/`)}
                size="small"
                startIcon={<AddIcon />}
              >
                {t`Create new tariff`}
              </CustomButton>
              {ENABLE_TARIFF_IMPORT && (
                <CustomButton
                  onClick={handleImportDialogOpen}
                  size="small"
                  startIcon={<UploadIcon />}
                >
                  {t`Import tariff`}
                </CustomButton>
              )}
            </Box>
            <ListSearch
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
              handleSearch={handleSearch}
              placeholder={t`Search by name or code`}
            />
          </ListActions>
          <TariffList
            rows={tariffs}
            isLoading={isLoading}
            params={params}
            handleRequestSort={handleRequestSort}
            handleChangePage={handleChangePage}
            count={total_results}
            page={params.start_index ? parseInt(params.start_index) / ROWS_PER_PAGE_DEFAULT : 0}
          />
        </Box>
      </Box>
      {ENABLE_TARIFF_IMPORT && (
        <TariffImportDialog open={importDialogOpen} handleClose={handleImportDialogClose} />
      )}
    </Box>
  )
}

export default TariffListPage
